import type { ECharts } from 'echarts';

const IGNORED_SERIES_NAMES = ['TopTipsEvent', 'Total'];

function getDateAxisData(
  chart: ECharts,
  xAxisData: string[],
  xAxisIndex: number,
): string[] {
  const xAxis = chart.getOption().xAxis;
  const axis = Array.isArray(xAxis) ? xAxis[xAxisIndex] : xAxis;
  const data = (axis as IRecord | undefined)?.data;

  return Array.isArray(data) ? data.map(item => String(item)) : xAxisData;
}

export function resolveOpinionXAxisDataIndex(
  chart: ECharts,
  xAxisValue: string,
  xAxisData: string[],
  xAxisIndex: number,
): number {
  const axisData = getDateAxisData(chart, xAxisData, xAxisIndex);
  const directIndex = axisData.indexOf(xAxisValue);
  if (directIndex >= 0) {
    return directIndex;
  }

  const monthKey = xAxisValue.slice(0, 7);
  if (monthKey !== xAxisValue) {
    const monthIndex = axisData.indexOf(monthKey);
    if (monthIndex >= 0) {
      return monthIndex;
    }
  }

  return -1;
}

export function getOpinionChartSeriesIndex(chart: ECharts): number {
  const series = (chart.getOption().series || []) as IRecord[];
  const index = series.findIndex(
    item =>
      item?.name && !IGNORED_SERIES_NAMES.includes(String(item.name)),
  );

  return index >= 0 ? index : 0;
}

export function getOpinionDateAxisIndex(chart: ECharts): number {
  const xAxis = chart.getOption().xAxis;
  return Array.isArray(xAxis) && xAxis.length > 1 ? 1 : 0;
}

/** 事件气泡 hover 时联动底部日期轴 axisPointer（黑色加粗日期） */
export function syncOpinionChartDateAxisPointer(
  chart: ECharts | null,
  xAxisValue: string,
  xAxisData: string[],
  showEventAxis: boolean,
): void {
  if (!chart || !xAxisValue) {
    return;
  }

  const xAxisIndex = getOpinionDateAxisIndex(chart);
  const dataIndex = resolveOpinionXAxisDataIndex(
    chart,
    xAxisValue,
    xAxisData,
    xAxisIndex,
  );
  if (dataIndex < 0) {
    return;
  }

  const seriesIndex = getOpinionChartSeriesIndex(chart);

  try {
    const pixel = chart.convertToPixel({ seriesIndex }, [dataIndex, 0]) as
      | number[]
      | undefined;
    if (
      Array.isArray(pixel) &&
      Number.isFinite(pixel[0]) &&
      Number.isFinite(pixel[1])
    ) {
      chart.dispatchAction({
        type: 'updateAxisPointer',
        currTrigger: 'mousemove',
        x: pixel[0],
        y: pixel[1],
      });
      return;
    }
  } catch {
    // convertToPixel 失败时回退 dataIndex 方式
  }

  chart.dispatchAction({
    type: 'updateAxisPointer',
    currTrigger: 'mousemove',
    xAxisIndex,
    seriesIndex,
    dataIndex,
  });
}

const clearingOpinionChartDateAxisPointer = new WeakSet<ECharts>();
const opinionChartAxisPointerSuppress = new WeakMap<ECharts, boolean>();
const opinionChartAxisPointerKeep = new WeakMap<ECharts, boolean>();
const opinionChartAxisPointerClearTimer = new WeakMap<
  ECharts,
  ReturnType<typeof setTimeout>
>();
const registeredSuppressListeners = new WeakSet<ECharts>();

export const OPINION_CHART_AXIS_POINTER_CLEAR_DELAY_MS = 200;

export interface IOpinionChartAxisPointerController {
  sync: (time: string) => void;
  scheduleClear: (delay?: number) => void;
  clear: () => void;
  setSuppress: (value: boolean) => void;
  setKeep: (value: boolean) => void;
}

function ensureOpinionChartAxisPointerSuppressListener(
  chart: ECharts | null,
): void {
  if (!chart || registeredSuppressListeners.has(chart)) {
    return;
  }

  registeredSuppressListeners.add(chart);
  chart.on('updateAxisPointer', () => {
    if (isOpinionChartAxisPointerSuppressed(chart)) {
      clearOpinionChartDateAxisPointer(chart);
    }
  });
}

export function createOpinionChartAxisPointerController(
  chart: ECharts | null,
  xAxisData: string[],
  showEventAxis: boolean,
): IOpinionChartAxisPointerController {
  ensureOpinionChartAxisPointerSuppressListener(chart);

  return {
    sync(time: string): void {
      if (!chart || isOpinionChartAxisPointerSuppressed(chart)) {
        return;
      }
      clearOpinionChartDateAxisPointerLeaveTimer(chart);
      syncOpinionChartDateAxisPointer(chart, time, xAxisData, showEventAxis);
    },
    scheduleClear(
      delay = OPINION_CHART_AXIS_POINTER_CLEAR_DELAY_MS,
    ): void {
      scheduleOpinionChartDateAxisPointerClear(chart, delay);
    },
    clear(): void {
      clearOpinionChartDateAxisPointerLeaveTimer(chart);
      clearOpinionChartDateAxisPointer(chart);
    },
    setSuppress(value: boolean): void {
      setOpinionChartAxisPointerSuppress(chart, value);
    },
    setKeep(value: boolean): void {
      setOpinionChartAxisPointerKeep(chart, value);
    },
  };
}

export function shouldDeferOpinionChartAxisPointerClear(
  chart: ECharts | null,
): boolean {
  return isOpinionChartAxisPointerKept(chart);
}

export function clearOpinionChartDateAxisPointerLeaveTimer(
  chart: ECharts | null,
): void {
  if (!chart) {
    return;
  }

  const timer = opinionChartAxisPointerClearTimer.get(chart);
  if (timer) {
    clearTimeout(timer);
    opinionChartAxisPointerClearTimer.delete(chart);
  }
}

export function setOpinionChartAxisPointerKeep(
  chart: ECharts | null,
  keep: boolean,
): void {
  if (!chart) {
    return;
  }

  opinionChartAxisPointerKeep.set(chart, keep);
  if (keep) {
    clearOpinionChartDateAxisPointerLeaveTimer(chart);
  }
}

export function isOpinionChartAxisPointerKept(chart: ECharts | null): boolean {
  return chart ? !!opinionChartAxisPointerKeep.get(chart) : false;
}

export function scheduleOpinionChartDateAxisPointerClear(
  chart: ECharts | null,
  delay = OPINION_CHART_AXIS_POINTER_CLEAR_DELAY_MS,
): void {
  if (!chart) {
    return;
  }

  clearOpinionChartDateAxisPointerLeaveTimer(chart);
  opinionChartAxisPointerClearTimer.set(
    chart,
    setTimeout(() => {
      opinionChartAxisPointerClearTimer.delete(chart);
      if (
        !isOpinionChartAxisPointerSuppressed(chart) &&
        !isOpinionChartAxisPointerKept(chart)
      ) {
        clearOpinionChartDateAxisPointer(chart);
      }
    }, delay),
  );
}

export function setOpinionChartAxisPointerSuppress(
  chart: ECharts | null,
  suppressed: boolean,
): void {
  if (!chart) {
    return;
  }

  opinionChartAxisPointerSuppress.set(chart, suppressed);
  if (suppressed) {
    clearOpinionChartDateAxisPointerLeaveTimer(chart);
    clearOpinionChartDateAxisPointer(chart);
  }
}

export function isOpinionChartAxisPointerSuppressed(
  chart: ECharts | null,
): boolean {
  return chart ? !!opinionChartAxisPointerSuppress.get(chart) : false;
}

export function clearOpinionChartDateAxisPointer(chart: ECharts | null): void {
  if (!chart) {
    return;
  }

  if (clearingOpinionChartDateAxisPointer.has(chart)) {
    return;
  }

  clearingOpinionChartDateAxisPointer.add(chart);
  try {
    chart.dispatchAction({
      type: 'updateAxisPointer',
      currTrigger: 'leave',
    });
  } finally {
    clearingOpinionChartDateAxisPointer.delete(chart);
  }
}
